import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#syncExplicitChoiceOnLoad();
  }

  // ——— Choix explicite (data-require-choice, rendu par _product-variant-picker.liquid) ———
  // Sans ?variant= dans l'URL, Taille et Quantité arrivent sans présélection :
  // tant que toutes les options ne sont pas choisies, aucune variante n'est
  // résolue et le bouton d'ajout au panier reste désactivé avec un libellé
  // « Choisissez votre taille / quantité ».

  get requiresExplicitChoice() {
    return this.hasAttribute('data-require-choice');
  }

  // Noms des options (sans le préfixe "option-") n'ayant aucune valeur choisie.
  get missingOptionNames() {
    const groups = new Map();

    for (const input of this.querySelectorAll('fieldset input[type="radio"][name^="option-"]')) {
      groups.set(input.name, groups.get(input.name) || input.checked);
    }

    for (const select of this.querySelectorAll('select[name^="option-"]')) {
      groups.set(select.name, select.value !== '');
    }

    const missing = [];
    for (const [name, hasSelection] of groups) {
      if (!hasSelection) missing.push(name.replace(/^option-/, ''));
    }
    return missing;
  }

  get isSelectionComplete() {
    return this.missingOptionNames.length === 0;
  }

  #syncExplicitChoiceOnLoad() {
    if (!this.requiresExplicitChoice) return;

    // Quantity breaks en mode « afficher les variantes » : le picker principal
    // est masqué et ignoré à l'ajout au panier → pas de choix explicite à exiger
    const quantityBreaks = this.closest('product-form')?.querySelector('[data-ref="quantity-breaks"]');
    if (quantityBreaks?.dataset.showVariantPicker === 'true') {
      this.removeAttribute('data-require-choice');
      this.#setNeedsChoice(false, true);
      return;
    }

    this.#refreshChoiceState();
  }

  // Met à jour l'état du bouton ; renvoie true si la sélection est complète.
  #refreshChoiceState() {
    const missing = this.missingOptionNames;
    if (missing.length === 0) return true;

    this.#setNeedsChoice(true, false, missing);
    return false;
  }

  #setNeedsChoice(active, available, missing = []) {
    const formElement = this.closest('product-form');
    if (!formElement) return;

    // Ne pas interférer avec la restriction « cadeau uniquement »
    if (window.__giftOnlyProducts?.[formElement.dataset.productId]) return;

    for (const button of formElement.querySelectorAll('[data-ref="add-to-cart-button"]')) {
      if (active) {
        button.setAttribute('data-needs-choice', '');
        button.disabled = true;

        const label = button.querySelector('[data-ref="choose-label"]');
        if (label) label.textContent = this.#chooseLabel(label, missing);
      } else if (button.hasAttribute('data-needs-choice')) {
        button.removeAttribute('data-needs-choice');
        button.disabled = available === false;
      }
    }
  }

  // « Choisissez votre taille » prioritaire, puis la quantité, sinon générique.
  #chooseLabel(label, missing) {
    const names = missing.map((name) => name.toLowerCase());

    if (names.some((name) => name === 'taille' || name === 'pointure')) {
      return label.dataset.labelTaille || label.textContent;
    }
    if (names.some((name) => name.includes('quantit'))) {
      return label.dataset.labelQuantite || label.textContent;
    }
    return label.dataset.labelGeneric || label.textContent;
  }

  // Retire ?variant= de l'URL tant que la sélection est incomplète (page produit uniquement).
  #stripVariantFromUrl() {
    const isOnProductPage =
      Theme.template.name === 'product' && !this.closest('product-card') && !this.closest('quick-add-dialog');
    if (!isOnProductPage) return;

    const url = new URL(window.location.href);
    if (url.searchParams.has('variant')) {
      url.searchParams.delete('variant');
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Choix explicite : tant que toutes les options ne sont pas choisies,
    // aucune variante n'est résolue → pas de requête, bouton désactivé
    if (this.requiresExplicitChoice && !this.#refreshChoiceState()) {
      const idInput = this.querySelector('input[name="id"]');
      if (idInput) idInput.value = '';
      this.#stripVariantFromUrl();
      return;
    }

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Cible résolue depuis un ID de valeur d'option : pour un select, l'élément
    // trouvé est l'<option> elle-même
    if (target instanceof HTMLOptionElement) {
      const select = target.closest('select');
      if (select) {
        for (const option of select.options) {
          option.removeAttribute('selected');
        }
        target.setAttribute('selected', 'selected');
        select.value = target.value;
      }
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);

    // Si variant-picker est un enfant de quick-add-component ou swatches-variant-picker-component,
    // nous devons ajouter section_id=section-rendering-product-card à l'URL
    // if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
    //   if (productUrl?.includes('?')) {
    //     productUrl = productUrl.split('?')[0];
    //   }
    //   return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    // }
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Récupère les données JSON de la variante depuis la réponse
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Sauvegarde la sélection : la réponse est rendue sans ?variant=, donc
        // en mode choix explicite elle revient sans aucune présélection
        const selectedOptionValueIds = this.requiresExplicitChoice ? this.selectedOptionsValues : null;

        // Met à jour seulement le variant picker
        this.updateVariantPicker(html);

        const variantData = JSON.parse(textContent);

        // Choix explicite : réapplique la sélection de l'acheteur et renseigne
        // l'ID de la variante résolue dans le champ caché du formulaire
        if (selectedOptionValueIds) {
          for (const optionValueId of selectedOptionValueIds) {
            try {
              this.updateSelectedOption(optionValueId);
            } catch {
              // valeur absente du nouveau rendu : on l'ignore
            }
          }

          const idInput = this.querySelector('input[name="id"]');
          if (idInput && variantData?.id) idInput.value = variantData.id;
        }

        // Déclenche un événement pour notifier la mise à jour de la variante
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(variantData, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
            }),
          );
        }

        // Le bouton d'ajout au panier fraîchement re-rendu (via variantUpdated)
        // arrive désactivé côté serveur : la sélection est complète, on le réactive
        if (this.requiresExplicitChoice) {
          this.#setNeedsChoice(false, variantData?.available !== false);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
